import { SdkContext } from '../internal'
import type {
  AnalyticsFunnel,
  AnalyticsFunnelMembers,
  AnalyticsMediaFeeds,
  AnalyticsOnline,
  AnalyticsOverview,
  AnalyticsPageviews,
  AnalyticsRealtime,
  AnalyticsContentDetail,
  AnalyticsTopContent,
  FunnelStepKey,
  PageviewsGroupBy
} from '@/types/analytics'

type Period = { from?: string; to?: string }

const clean = (q: Record<string, unknown>) => Object.fromEntries(Object.entries(q).filter(([, v]) => v !== undefined && v !== null && v !== ''))

/** The funnel takes several products as `productIds=a,b`; one id also goes as `productId`, which older servers read. */
type ProductFilter = { productIds?: string[] }
const productQuery = ({ productIds, ...rest }: Record<string, unknown> & ProductFilter) =>
  clean({
    ...rest,
    productIds: productIds?.length ? productIds.join(',') : undefined,
    productId: productIds?.length === 1 ? productIds[0] : undefined
  })

/**
 * F-88: the community's own analytics (reads). Owner and admin read everything; finance
 * only the funnel. The collector (`POST /analytics/events`) lives in `lib/analytics/collector`.
 */
export class AnalyticsResource {
  constructor(private ctx: SdkContext) {}

  overview(params: Period & { limit?: number }): Promise<AnalyticsOverview> {
    return this.ctx.raw<AnalyticsOverview>({ httpMethod: 'GET', path: '/analytics/overview' }, { query: clean(params) })
  }

  pageviews(params: Period & { groupBy?: PageviewsGroupBy; limit?: number }): Promise<AnalyticsPageviews> {
    return this.ctx.raw<AnalyticsPageviews>({ httpMethod: 'GET', path: '/analytics/pageviews' }, { query: clean(params) })
  }

  funnel(params: Period & ProductFilter): Promise<AnalyticsFunnel> {
    return this.ctx.raw<AnalyticsFunnel>({ httpMethod: 'GET', path: '/analytics/funnel' }, { query: productQuery(params) })
  }

  funnelMembers(params: Period & ProductFilter & { step: FunnelStepKey; page?: number; limit?: number; search?: string }): Promise<AnalyticsFunnelMembers> {
    return this.ctx.raw<AnalyticsFunnelMembers>({ httpMethod: 'GET', path: '/analytics/funnel/members' }, { query: productQuery(params) })
  }

  /** F-179: the period's most read posts (owner and admin). */
  topContent(params: Period & { limit?: number }): Promise<AnalyticsTopContent> {
    return this.ctx.raw<AnalyticsTopContent>({ httpMethod: 'GET', path: '/analytics/content' }, { query: clean(params) })
  }

  /** F-179: one post's read metrics, since it was created unless a period is given (owner, admin, author). */
  content(contentId: string, params: Period = {}): Promise<AnalyticsContentDetail> {
    return this.ctx.raw<AnalyticsContentDetail>({ httpMethod: 'GET', path: `/analytics/content/${encodeURIComponent(contentId)}` }, { query: clean(params) })
  }

  online(): Promise<AnalyticsOnline> {
    return this.ctx.raw<AnalyticsOnline>({ httpMethod: 'GET', path: '/analytics/online' })
  }

  realtime(minutes = 30): Promise<AnalyticsRealtime> {
    return this.ctx.raw<AnalyticsRealtime>({ httpMethod: 'GET', path: '/analytics/realtime' }, { query: { minutes } })
  }

  /** F-178 phase 2: the RSS consumption (owner and admin): downloads, listeners, apps, by day, show and episode. */
  mediaFeeds(params: Period & { showId?: string; episodeId?: string }): Promise<AnalyticsMediaFeeds> {
    return this.ctx.raw<AnalyticsMediaFeeds>({ httpMethod: 'GET', path: '/analytics/media-feeds' }, { query: clean(params) })
  }
}
